/**
 * iter250 — Activation screen.
 *
 * Where every blocked sign-in lands. Two modes:
 *   1. `?email=` — the account exists and just needs its link clicked. Offers
 *      an unauthenticated resend (the dashboard banner's authed resend is
 *      unreachable for exactly these users, since they hold no token).
 *   2. `?claim=` — an X/Twitter signup. X returns no email address at all, so
 *      there is nothing to resend: the user supplies a real address first and
 *      the link goes there.
 */
import React, { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { MailCheck, Loader2, ShieldCheck, Inbox } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import DotboxLogo from '@/components/common/DotboxLogo';
import { api } from '@/lib/api';

const ActivatePage = () => {
  const [searchParams] = useSearchParams();
  const claimToken = searchParams.get('claim') || '';
  const presetEmail = searchParams.get('email') || '';

  const [email, setEmail] = useState(claimToken ? '' : presetEmail);
  const [busy, setBusy] = useState(false);
  const [sentTo, setSentTo] = useState('');

  const handleResend = async () => {
    if (!email || !email.includes('@')) {
      toast.error('Enter the email address you signed up with');
      return;
    }
    setBusy(true);
    try {
      await api.post('/auth/resend-activation', { email: email.trim().toLowerCase() });
      setSentTo(email.trim().toLowerCase());
      toast.success('Activation link sent — check your inbox');
    } catch {
      toast.error('Could not send the link right now. Please try again shortly.');
    } finally {
      setBusy(false);
    }
  };

  const handleClaim = async () => {
    if (!email || !email.includes('@')) {
      toast.error('Enter a valid email address');
      return;
    }
    setBusy(true);
    try {
      const res = await api.post('/auth/claim-email', {
        claim_token: claimToken,
        email: email.trim().toLowerCase(),
      });
      setSentTo(res.data?.email || email.trim().toLowerCase());
      toast.success('Activation link sent — check your inbox');
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Could not use that address');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A1024] constellation-bg flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-6">
          <DotboxLogo className="mx-auto h-10" variant="dark" />
        </div>
        <Card data-testid="activate-page">
          <CardContent className="p-8">
            {sentTo ? (
              <div className="text-center" data-testid="activate-sent-state">
                <div className="mx-auto mb-4 w-16 h-16 rounded-full bg-emerald-500/20 flex items-center justify-center">
                  <Inbox className="h-9 w-9 text-emerald-400" />
                </div>
                <h1 className="text-xl font-bold text-white mb-2">Link on its way</h1>
                <p className="text-slate-400 text-sm mb-6">
                  We sent an activation link to{' '}
                  <strong className="text-white">{sentTo}</strong>. Click it and your
                  7-day trial starts from that moment.
                </p>
                <Link to="/login">
                  <Button
                    className="w-full bg-[#DC2626] hover:bg-[#B91C1C]"
                    data-testid="activate-back-to-login-btn"
                  >
                    Back to sign in
                  </Button>
                </Link>
              </div>
            ) : (
              <>
                <div className="text-center mb-6">
                  <div className="mx-auto mb-4 w-16 h-16 rounded-full bg-amber-500/15 flex items-center justify-center">
                    <ShieldCheck className="h-9 w-9 text-amber-300" />
                  </div>
                  <h1
                    className="text-xl font-bold text-white mb-2"
                    data-testid="activate-title"
                  >
                    {claimToken ? 'One more thing' : 'Activate your account'}
                  </h1>
                  <p className="text-slate-400 text-sm">
                    {claimToken
                      ? 'X doesn\u2019t share an email address with us, so we need one to confirm it\u2019s really you.'
                      : 'Your account is ready but not active yet. Click the link in the email we sent, or send yourself a fresh one.'}
                  </p>
                </div>

                <div className="space-y-2 mb-4">
                  <Label htmlFor="activate-email">
                    {claimToken ? 'Your email address' : 'Email you signed up with'}
                  </Label>
                  <Input
                    id="activate-email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@company.com"
                    className="h-11"
                    disabled={busy}
                    data-testid="activate-email-input"
                  />
                  {claimToken && (
                    <p className="text-xs text-slate-500">
                      Disposable / throwaway emails aren&apos;t allowed.
                    </p>
                  )}
                </div>

                <Button
                  onClick={claimToken ? handleClaim : handleResend}
                  disabled={busy}
                  className="w-full h-11 bg-[#DC2626] hover:bg-[#B91C1C] font-semibold"
                  data-testid="activate-submit-btn"
                >
                  {busy ? (
                    <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Sending…</>
                  ) : (
                    <><MailCheck className="mr-2 h-4 w-4" /> {claimToken ? 'Send activation link' : 'Resend activation link'}</>
                  )}
                </Button>

                <p className="text-xs text-slate-500 text-center mt-4">
                  Already clicked it?{' '}
                  <Link
                    to="/login"
                    className="font-semibold text-red-400 hover:text-red-300"
                    data-testid="activate-login-link"
                  >
                    Sign in
                  </Link>
                </p>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ActivatePage;
